import type { TMap, TPromise, TSet } from '../types'

export class LRUCache {
  private addedKeys: TSet = new Set()
  private cache: TMap = new Map()
  private removedKeys: TSet = new Set()
  private updatePromise: TPromise | undefined

  constructor(
    public limit = 100,
    public onUpdate?: (addedKeys: string[], removedKeys: string[]) => TPromise
  ) {}

  clear(): void {
    this.keys().forEach((key) => this.trackRemove(key))
    this.cache.clear()
    this.triggerUpdate()
  }

  delete(key: string): void {
    if (this.cache.has(key)) {
      this.cache.delete(key)
      this.trackRemove(key)
      this.triggerUpdate()
    }
  }

  entries<T = any>(): [string, T][] {
    return Array.from(this.cache.entries())
  }

  get<T = any>(key: string): T | undefined {
    if (!this.cache.has(key)) return
    const value = this.cache.get(key)
    this.cache.delete(key)
    this.cache.set(key, value)
    return value
  }

  keys(): string[] {
    return Array.from(this.cache.keys())
  }

  set<T = any>(key: string, value: T, track = true): void {
    if (this.cache.has(key)) {
      this.cache.delete(key)
    } else if (this.cache.size > 0 && this.cache.size >= this.limit) {
      while (this.cache.size >= this.limit) {
        const oldKey = this.cache.keys().next().value
        this.cache.delete(oldKey)
        if (track) this.trackRemove(oldKey)
      }
    }
    this.cache.set(key, value)
    if (track) this.trackAdd(key)
    this.triggerUpdate()
  }

  size(): number {
    return this.cache.size
  }

  values<T = any>(): T[] {
    return Array.from(this.cache.values())
  }

  private trackAdd(key: string): void {
    this.addedKeys.add(key)
    this.removedKeys.delete(key)
  }

  private trackRemove(key: string): void {
    this.removedKeys.add(key)
    this.addedKeys.delete(key)
  }

  private triggerUpdate(): void {
    this.updatePromise ??= Promise.resolve().then(async () => {
      try {
        const addedKeys = Array.from(this.addedKeys)
        const removedKeys = Array.from(this.removedKeys)
        this.addedKeys.clear()
        this.removedKeys.clear()
        if (addedKeys.length > 0 || removedKeys.length > 0) {
          await this.onUpdate?.(addedKeys, removedKeys)
        }
      } catch {
      } finally {
        this.updatePromise = undefined
        if (this.addedKeys.size > 0 || this.removedKeys.size > 0) {
          this.triggerUpdate()
        }
      }
    })
  }
}
